import { useCallback, useSyncExternalStore } from 'react';
import { pushRecent } from './pushRecent';

const STORAGE_KEY = 'po.recentModules.v1';
const listeners = new Set<() => void>();
let cache: string | null = null;

/** Збережене: користувач → робочий простір → ключі модулів (найновіший перший); недоступне сховище — порожньо. */
function read(): Record<string, Record<string, string[]>> {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, Record<string, string[]>>) : {};
  } catch {
    return {};
  }
}

function snapshot(): string {
  try { cache = localStorage.getItem(STORAGE_KEY) ?? '{}'; } catch { cache = cache ?? '{}'; }
  return cache;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  window.addEventListener('storage', listener);
  return () => { listeners.delete(listener); window.removeEventListener('storage', listener); };
}

/** Запам'ятовує відкриття модуля (прототип v4 openModule → recent[ws]); без сховища — нічого не робить. */
export function rememberModule(userId: string, ws: string, key: string): void {
  const all = read();
  const mine = all[userId] ?? {};
  const next = pushRecent(mine[ws] ?? [], key);
  if ((mine[ws] ?? []).join() === next.join()) return;
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...all, [userId]: { ...mine, [ws]: next } })); } catch { return; }
  listeners.forEach((l) => l());
}

/**
 * Нещодавно відкриті модулі користувача в робочому просторі (чипи «Нещодавні» навігатора Ctrl+K): localStorage окремо для кожного
 * користувача й простору, синхронізується між вкладками.
 */
export function useRecentModules(userId: string | undefined, ws: string): string[] {
  const raw = useSyncExternalStore(subscribe, snapshot, () => '{}');
  const pick = useCallback((text: string) => {
    try { return (JSON.parse(text) as Record<string, Record<string, string[]>>)[userId ?? '']?.[ws] ?? []; } catch { return []; }
  }, [userId, ws]);
  return userId ? pick(raw) : [];
}
